import { SectionWrapper } from '@common/components/BookingForms';
import { BlockInputField } from '@common/components/FormElements/BlockInputField';
import { BlockSelectField } from '@common/components/FormElements/BlockSelectField';
import { RadioGroupField } from '@common/components/RadioGroupField';
import { SpaEntityAddExistingGuests } from '@common/components/SpaEntity/components/SpaEntityAddExistingGuests';
import { genderOptions, genderOptionsWithNeutral } from '@common/constants';
import { useTranslations } from '@lang';
import { basketSelectors } from '@store/basket/selectors';
import { guestSelectors } from '@store/guest/selectors';
import { maleTitlesOptions, femaleTitlesOptions } from '@utils/guestTitles';
import { FC, useEffect } from 'react';
import { Controller, useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { Guest } from 'shared-types/Basket';
import { SpaPackageType } from 'shared-types/SpaPackage';
import { SpaPackageFormValues } from '../../../SpaPackageForm.schema';
import { ConfigurationGroup } from './GuestDetails/ConfigurationGroup/ConfigurationGroup';
import {
  GuestColumn,
  GuestCounter,
  GuestRow,
  GenderRow,
  InputsRow,
  TitleText,
} from './GuestDetails/GuestFields/GuestFields.styles';
import { filterTimeslotOptionsByStaffAndRoomAvailability } from './TimeSlotFiltering/timeslotFiltering';
import { generateSelectedByStaffAndRooms } from './TimeSlotFiltering/timeslotFiltering';

type GuestDetailsAndSelectionsProps = {
  quantity: number;
  entity: SpaPackageType;
  display: boolean;
  showGenderNeutral: boolean;
};

export const GuestDetailsAndSelections: FC<GuestDetailsAndSelectionsProps> = ({
  quantity,
  entity,
  display,
  showGenderNeutral,
}) => {
  const { t } = useTranslations();
  const { control, setValue, formState } = useFormContext<SpaPackageFormValues>();
  const fieldArray = useFieldArray({ control, name: 'guests' });
  const { fields, append, remove } = fieldArray;
  const guestCount = fields.length;
  const guestValues = useWatch({ control, name: 'guests' }) ?? [];
  const loggedInGuest = useSelector(guestSelectors.getGuest) as Guest | null;
  const availableGuests = useSelector(basketSelectors.getBasketGuests) as Guest[];

  useEffect(() => {
    if (quantity > guestCount) {
      for (let i = guestCount; i < quantity; i++) {
        append({ title: '', firstName: '', lastName: '', gender: '', placeholder: true });
      }
    } else if (quantity < guestCount) {
      for (let i = guestCount - 1; i >= quantity; i--) {
        remove(i);
      }
    }
  }, [quantity, guestCount, append, remove]);

  const titlesOptions = [...maleTitlesOptions, ...femaleTitlesOptions].map((option, index) => ({
    ...option,
    label: t(option.label),
    key: index,
  }));

  const { configurationGroups, configurationGroupsRequireTimeslots, supportsTimeslots } = entity;
  const configurationGroupsNoExtras = configurationGroups.filter((group) => !group.isExtras);
  const configurationGroupsSupportsSelections = configurationGroupsNoExtras.some(
    (group) => group.supportsSelections
  );
  const noArrivalTimesRequiresTimeslots = !supportsTimeslots && configurationGroupsRequireTimeslots;
  const requiresTimeslotsHasTimeslots =
    configurationGroupsRequireTimeslots &&
    configurationGroupsNoExtras.some((group) => group.supportsTimeslots);

  const generatedSelected = generateSelectedByStaffAndRooms(guestValues as any);
  const selected = generatedSelected ? { ...generatedSelected, any: 0 } : null;

  const selectedGuests = (guestValues as Guest[]).map(
    ({ title, firstName, lastName, gender, id, loggedInUser }) => ({
      title,
      firstName,
      lastName,
      gender,
      id,
      loggedInUser,
    })
  ) as Guest[];

  if (!display) {
    return null;
  }

  return (
    <>
      {(availableGuests?.length > 0 || loggedInGuest) && (
        <SectionWrapper>
          <SpaEntityAddExistingGuests
            fieldArray={fieldArray as any}
            guestValues={guestValues as any}
            selectedGuests={selectedGuests}
            existingGuests={availableGuests || []}
            guest={loggedInGuest}
            minimumGuests={fields.length}
            maximumGuests={fields.length}
            isGenderSpecific={entity.gender}
          />
        </SectionWrapper>
      )}
      {fields.map((fieldEntry, index) => {
        const guestData = (guestValues[index] || {}) as any;
        const isExistingBasketGuest = !!(guestData.id || guestData.externalSpaId);

        return (
          <SectionWrapper key={fieldEntry.id}>
            <div>
              <GuestRow>
                <GuestCounter>
                  <TitleText>{t('spa.guestCounter', { counter: index + 1 })}</TitleText>
                </GuestCounter>
                <InputsRow>
                  <GuestColumn>
                    <Controller
                      control={control}
                      name={`guests.${index}.title`}
                      render={({ field: { onChange, value }, fieldState: { error } }) => (
                        <BlockSelectField
                          useNativeDropdownOnMobile={false}
                          labelWidth="50px"
                          options={titlesOptions}
                          value={titlesOptions.find((opt) => opt.value === value) ?? null}
                          label={t('common.title')}
                          isDisabled={isExistingBasketGuest}
                          isSearchable={false}
                          id={`${index}_Guest_title`}
                          isRequired
                          error={formState.submitCount > 0 ? error?.message : undefined}
                          onChange={(selected: any) => {
                            const val = selected?.value ?? '';
                            onChange(val);
                            if (!entity.gender) {
                              setValue(`guests.${index}.gender`, val === 'Mr' ? 'male' : 'female', {
                                shouldValidate: true,
                                shouldDirty: true,
                              });
                            }
                          }}
                        />
                      )}
                    />
                  </GuestColumn>
                  <GuestColumn>
                    <Controller
                      control={control}
                      name={`guests.${index}.firstName`}
                      render={({ field: { onChange, value }, fieldState: { error } }) => (
                        <BlockInputField
                          label={t('common.firstName')}
                          placeholder={t('common.firstName')}
                          disabled={isExistingBasketGuest}
                          id={`${index}_Guest_fname`}
                          value={value ?? ''}
                          onChange={onChange}
                          isRequired
                          error={formState.submitCount > 0 ? error?.message : undefined}
                        />
                      )}
                    />
                  </GuestColumn>
                  <GuestColumn>
                    <Controller
                      control={control}
                      name={`guests.${index}.lastName`}
                      render={({ field: { onChange, value }, fieldState: { error } }) => (
                        <BlockInputField
                          label={t('common.lastName')}
                          placeholder={t('common.lastName')}
                          disabled={isExistingBasketGuest}
                          id={`${index}_Guest_lname`}
                          value={value ?? ''}
                          onChange={onChange}
                          isRequired
                          error={formState.submitCount > 0 ? error?.message : undefined}
                        />
                      )}
                    />
                  </GuestColumn>
                </InputsRow>
                <GenderRow>
                  <Controller
                    control={control}
                    name={`guests.${index}.gender`}
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <RadioGroupField
                        name={`guests.${index}.gender`}
                        input={{ value: value ?? '', onChange }}
                        meta={{ error: error?.message ?? '', touched: formState.submitCount > 0 }}
                        error={formState.submitCount > 0 && !!error}
                        options={showGenderNeutral ? genderOptionsWithNeutral : genderOptions}
                        label={t('common.gender')}
                        isDisabled={!showGenderNeutral && isExistingBasketGuest}
                      />
                    )}
                  />
                </GenderRow>
              </GuestRow>
            </div>
            {(configurationGroupsSupportsSelections ||
              noArrivalTimesRequiresTimeslots ||
              requiresTimeslotsHasTimeslots) &&
              configurationGroupsNoExtras
                .filter((cg) => cg.isRequired)
                .map(
                  (
                    {
                      options,
                      supportsSelections,
                      minimumSelections,
                      maximumSelections,
                      id,
                      supportsTimeslots: groupSupportsTimeslots,
                    },
                    groupIndex
                  ) => {
                    const filteredOptions = filterTimeslotOptionsByStaffAndRoomAvailability(
                      options,
                      selected,
                      id,
                      index
                    );
                    return (
                      <ConfigurationGroup
                        key={id}
                        fieldName={`guests.${index}.configurationGroups.group_${id}`}
                        configurationGroupId={id}
                        options={filteredOptions}
                        maximumSelections={maximumSelections}
                        minimumSelections={minimumSelections}
                        supportsSelections={supportsSelections}
                        requireTimeSlots={configurationGroupsRequireTimeslots}
                        index={groupIndex}
                        supportsTimeslots={groupSupportsTimeslots}
                      />
                    );
                  }
                )}
          </SectionWrapper>
        );
      })}
    </>
  );
};
